import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./index.css";

function SortableCategory({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function SortableSubcategory({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <TableRow ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </TableRow>
  );
}

const CategorySubcategory = () => {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [subcategories, setSubcategories] = useState([{ name: "" }]);
  const [editIndex, setEditIndex] = useState(null);

  const navigate = useNavigate();
  const userDataString = localStorage.getItem("Login_User_Community_Details");
  const token = JSON.parse(userDataString);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [categoryError, setCategoryError] = useState("");
  const [subcategoryError, setSubcategoryError] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [activeDragId, setActiveDragId] = React.useState(null);
  const [activeDragType, setActiveDragType] = React.useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getCatecory`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const formattedData = response.data.map((category) => ({
          ...category,
          subcategories: category.subcategories.map((sub) => ({
            sub_id: sub.id,
            id: sub.category_id,
            name: sub.subcategory_name,
          })),
        }));

        setCategories(formattedData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [token]);

  const handleDragStart = ({ active }) => {
    setActiveDragType(String(active.id).startsWith("category-") ? "category" : "subcategory");
    setActiveDragId(active.id);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveDragId(null);
    setActiveDragType(null);
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith("category-") && overId.startsWith("category-")) {
      const oldIndex = categories.findIndex((c) => `category-${c.id}` === activeId);
      const newIndex = categories.findIndex((c) => `category-${c.id}` === overId);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(categories, oldIndex, newIndex);
      setCategories(reordered);
      updateCategoryOrder(reordered);
    } else if (!activeId.startsWith("category-") && !overId.startsWith("category-")) {
      let sourceCatIndex = -1;
      let destCatIndex = -1;
      let sourceSubIndex = -1;
      let destSubIndex = -1;
      categories.forEach((cat, catIdx) => {
        cat.subcategories.forEach((sub, subIdx) => {
          const key = `subcategory-${sub.id},${sub.sub_id}`;
          if (key === activeId) {
            sourceCatIndex = catIdx;
            sourceSubIndex = subIdx;
          }
          if (key === overId) {
            destCatIndex = catIdx;
            destSubIndex = subIdx;
          }
        });
      });

      if (sourceCatIndex === -1 || destCatIndex === -1) return;

      const updated = categories.map((cat) => ({
        ...cat,
        subcategories: [...cat.subcategories],
      }));

      if (sourceCatIndex === destCatIndex) {
        updated[sourceCatIndex].subcategories = arrayMove(
          updated[sourceCatIndex].subcategories,
          sourceSubIndex,
          destSubIndex
        );
      } else {
        const [movedSubcategory] = updated[sourceCatIndex].subcategories.splice(
          sourceSubIndex,
          1
        );
        updated[destCatIndex].subcategories.splice(destSubIndex, 0, movedSubcategory);
        await updateSubcategoryCategory(
          movedSubcategory.id,
          updated[destCatIndex].id,
          movedSubcategory.sub_id
        );
      }

      setCategories(updated);
      updateCategoryOrder(updated);
    }
  };

  const updateSubcategoryCategory = async (subcategoryId, newCategoryId, subSubId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/updateSubcategoryPosition`,
        {
          subcategoryId,
          newCategoryId,
          subSubId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Subcategory position updated successfully");
    } catch (error) {
      console.error("Error updating subcategory position:", error);
    }
  };

  const updateCategoryOrder = (updatedCategories) => {
    axios
      .post(
        `${API_BASE_URL}/api/update-category-order`,
        { categories: updatedCategories },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((response) => {
        console.log("Order updated successfully");
        setCategories(updatedCategories);
      })
      .catch((error) => {
        console.error("Error updating order", error);
      });
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditIndex(null);
    resetForm();
  };

  const resetForm = () => {
    setCategoryName("");
    setSubcategories([{ name: "" }]);
  };

  const addSubcategory = () => {
    setSubcategories([...subcategories, { name: "" }]);
  };

  const handleSubcategoryChange = (index, value) => {
    const updatedSubcategories = [...subcategories];
    updatedSubcategories[index].name = value;
    setSubcategories(updatedSubcategories);
  };

  const handleSubmit = async () => {
    let hasError = false;

    setCategoryError("");
    setSubcategoryError("");

    if (!categoryName.trim()) {
      setCategoryError("Category Name is required.");
      hasError = true;
    }

    const validSubcategories = subcategories.filter((sub) => sub.name.trim() !== "");
    if (validSubcategories.length === 0) {
      setSubcategoryError("At least one subcategory is required.");
      hasError = true;
    }

    if (hasError) return;

    const userData = localStorage.getItem("Login_User_Community_Details");

    if (!userData) {
      console.error("User details not found in localStorage. Redirecting to login.");
      navigate("/login");
      return;
    }

    let parsedToken;
    try {
      parsedToken = JSON.parse(userData);
    } catch (error) {
      console.error("Error parsing user data. Redirecting to login.");
      navigate("/login");
      return;
    }

    if (!parsedToken) {
      console.error("Token is missing. Redirecting to login.");
      navigate("/login");
      return;
    }

    const newCategory = {
      name: categoryName.trim(),
      subcategories: validSubcategories,
    };

    try {
      if (editIndex !== null) {
        const response = await axios.put(
          `${API_BASE_URL}/api/updateCategory/${categories[editIndex].id}`,
          newCategory,
          {
            headers: {
              Authorization: `Bearer ${parsedToken}`,
            },
          }
        );

        const updatedCategories = [...categories];
        updatedCategories[editIndex] = response.data;
        setCategories(updatedCategories);
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/catsubadd`, newCategory, {
          headers: {
            Authorization: `Bearer ${parsedToken}`,
          },
        });

        setCategories([...categories, response.data]);
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text:
          editIndex !== null
            ? "Category updated successfully!"
            : "Category added successfully!",
      });

      handleClose();
    } catch (error) {
      console.error("Error saving category:", error.response || error.message);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "An error occurred while saving the category.",
      });
    }
  };

  const handleEdit = (index) => {
    if (index < 0 || index >= categories.length) {
      console.error("Invalid category index");
      return;
    }
    const category = categories[index];
    setCategoryName(category.name);
    setSubcategories(category.subcategories);
    setEditIndex(index);
    handleOpen();
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Category & Subcategory
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editIndex !== null ? "Edit" : "Add"} Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            fullWidth
            error={!!categoryError}
            helperText={categoryError}
            sx={{ marginBottom: 3 }}
          />
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Subcategories
          </Typography>
          {subcategories.map((subcategory, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <TextField
                label={`Subcategory ${index + 1}`}
                value={subcategory.name}
                onChange={(e) => handleSubcategoryChange(index, e.target.value)}
                fullWidth
                error={!!subcategoryError}
                helperText={subcategoryError}
              />
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addSubcategory}
            sx={{ marginBottom: 3 }}
          >
            Add Subcategory
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={categories.map((c) => `category-${c.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {categories.map((category, categoryIndex) => (
            <SortableCategory key={`category-${category.id}`} id={`category-${category.id}`}>
              <Box sx={{ marginBottom: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {category.name}
                </Typography>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Subcategory Name</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <SortableContext
                        items={category.subcategories.map(
                          (s) => `subcategory-${s.id},${s.sub_id}`
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {category.subcategories.map((sub) => (
                          <SortableSubcategory
                            key={`subcategory-${sub.id},${sub.sub_id}`}
                            id={`subcategory-${sub.id},${sub.sub_id}`}
                          >
                            <TableCell>{sub.name}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="error"
                                onClick={() => console.log(`Delete ${sub.id}`)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </SortableSubcategory>
                        ))}
                      </SortableContext>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </SortableCategory>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default CategorySubcategory;
